import { AccountExternalPagesVisibility, ClientAccount } from '@lune-climate/lune'
import { DropdownItemNew, DropdownNew, LuneThemeNew, Text } from '@lune-fe/lune-ui-lib'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import Co2OutlinedIcon from '@mui/icons-material/Co2Outlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import WebAssetOutlinedIcon from '@mui/icons-material/WebAssetOutlined'
import Box from '@mui/material/Box'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getClientAccountById } from 'endpoints/dapi'
import useIsTestMode from 'hooks/useIsTestMode'
import { getPublicPageUrl, PublicPagesType } from 'utils'
import { DataSheetInfo } from 'views/CalculateEmissions/Imports/ImportDetails'

import { getTypeColor } from '../Tables/ImportsTable/ImportTypeChip'

import ExtraInfoChip from './ExtraInfoChip'

const { palette } = LuneThemeNew

const ClientAccountDropdown = ({ clientAccountId }: { clientAccountId: string }) => {
    const [clientAccount, setClientAccount] = useState<ClientAccount>()
    const navigate = useNavigate()
    const isTestMode = useIsTestMode()

    const loadClientAccount = async () => {
        const result = await getClientAccountById(clientAccountId)
        if (result.isOk()) {
            setClientAccount(result.value)
        }
        // If clientAccount not found, we don't show the clientAccount chip
    }

    useEffect(() => {
        loadClientAccount()
    }, [])

    return clientAccount ? (
        <DropdownNew
            trigger={
                <Box
                    sx={{
                        cursor: 'pointer',
                        tabIndex: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1 / 2,
                        px: 1,
                        py: 1 / 2,
                        borderRadius: '100px',
                        backgroundColor: palette.Surface500 + '30', // 30% opacity
                        border: '1px solid',
                        borderColor: palette.Surface700,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        svg: {
                            height: '16px',
                            width: '16px',
                        },
                        '&:hover': {
                            backgroundColor: palette.Surface700 + '30',
                        },
                    }}
                >
                    <Text
                        variant={'body3'}
                        sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                        }}
                    >
                        {clientAccount.name}
                    </Text>
                    <ArrowDropDownIcon />
                </Box>
            }
        >
            <DropdownItemNew
                leftIcon={<Co2OutlinedIcon />}
                onClick={() => {
                    navigate(`/calculate-emissions?account_id=${clientAccount.id}`)
                }}
            >
                <Text variant={'body3'}>View all account emissions</Text>
            </DropdownItemNew>
            {clientAccount.externalPagesVisibility === AccountExternalPagesVisibility.PUBLIC && (
                <DropdownItemNew
                    leftIcon={<WebAssetOutlinedIcon />}
                    onClick={() => {
                        const url = getPublicPageUrl(
                            !!isTestMode,
                            clientAccount.organisationId,
                            clientAccount.handle!,
                            PublicPagesType.LOGISTICS,
                        )
                        window.open(url, '_blank')
                    }}
                >
                    <Text
                        variant={'body3'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        View customer page
                        <OpenInNewOutlinedIcon sx={{ fill: palette.TextTertiary }} />
                    </Text>
                </DropdownItemNew>
            )}
        </DropdownNew>
    ) : (
        <></>
    )
}

const ImportInfo: FC<{ info: DataSheetInfo }> = ({ info }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
            }}
        >
            {info.fileName && (
                <ExtraInfoChip
                    label={info.fileName}
                    color={getTypeColor(info.type)}
                    icon={<TableChartOutlinedIcon />}
                />
            )}
            {info.accountId && <ClientAccountDropdown clientAccountId={info.accountId} />}
            {info.templateName && (
                <ExtraInfoChip label={info.templateName} icon={<SchemaOutlinedIcon />} />
            )}
            {info.processedDate && (
                <ExtraInfoChip label={info.processedDate} icon={<CalendarMonthOutlinedIcon />} />
            )}
            {!!info.numberOfErrors && (
                <ExtraInfoChip
                    label={info.numberOfErrors === 1 ? '1 error' : `${info.numberOfErrors} errors`}
                    color={palette.TextError}
                    icon={<ErrorOutlineOutlinedIcon />}
                />
            )}
        </Box>
    )
}

export default ImportInfo
